import { editor, Uri, languages} from "monaco-editor";
import monacoStyles from "node_modules/monaco-editor/min/vs/editor/editor.main.css"

import { LitElement, css, unsafeCSS } from 'lit';
import { customElement, property, state } from "lit/decorators.js";
import { extractTextFromScripts, getDefaultThemeMode, ThemeMode } from '../utils';


const currentScriptUrl = new URL(import.meta.url);
const scriptPath = currentScriptUrl.pathname;
const scriptDir = scriptPath.substring(0, scriptPath.lastIndexOf('/'));

import Debug from 'debug';
const log = Debug('pdq:monaco');
self.MonacoEnvironment = {
  getWorkerUrl: function (moduleId, label) {
    console.log("getWorkerUrl", moduleId, label);
    if (label === "json") {
      return `${scriptDir}/monaco/json.worker.js`;
    }
    if (label === "css" || label === "scss" || label === "less") {
      return `${scriptDir}/monaco/css.worker.js`;
    }
    if (label === "html" || label === "handlebars" || label === "razor") {
      return `${scriptDir}/monaco/html.worker.js`;
    }
    if (label === "typescript" || label === "javascript") {
      return `${scriptDir}/monaco/ts.worker.js`;
    }
    return `${scriptDir}/monaco/editor.worker.js`;
  },
};

@customElement("pdq-text-editor")
export class PdqTextEditor extends LitElement {
  // static styles = unsafeCSS(monacoStyles)

  @property({ type: String })
  get height(): string {
    return this.style.height;
  }
  set height(value: string) {
    log(`pdq-monaco: changing height from ${this.style.height} to ${value}`)
    this.style.height = value;
  }

  @state()
  private _monacoEditor: editor.IStandaloneCodeEditor | null = null;

  @state()
  private _schema: Array<{ uri: string; schema: any; fileMatch?: string[] }> = [];

  @property({ type: String })
  value: string = '';

  @property({ type: String })
  theme: string =  getDefaultThemeMode() == ThemeMode.light ? 'vs' : 'vs-dark';

  @property({ type: String })
  language: string  = 'json';

  @property({ type: String })
  modelUri: string = `inmemory://monaco-${performance.now()}`
  container: HTMLDivElement|undefined

  get editor(): editor.IStandaloneCodeEditor | null {
    return this._monacoEditor;
  }
  get model() {
    return this._monacoEditor?.getModel();
  }
  get selectedText(): string {
    if (!this._monacoEditor) return "";
    return this._monacoEditor
    .getModel()
    ?.getValueInRange(this._monacoEditor.getSelection()!) || "";
  }
  addJsonSchema(uri: string, schema: object, validate = true, fileMatch?: string) {
    const newSchema: any = { uri, schema };
    if (fileMatch) newSchema.fileMatch = [Uri.parse(fileMatch).toString()];
    this._schema.push(newSchema);

    languages.json.jsonDefaults.setDiagnosticsOptions({
      validate,
      schemas: this._schema,
    });
  }
  setModel(value: string, language: string, uri: string | Uri) {
    this.model?.dispose();
    const model = editor.createModel(
      value,
      language,
      typeof uri === "string" ? Uri.parse(uri) : uri
    );
    this._monacoEditor?.setModel(model);
  }
  setOptions(options: editor.IEditorOptions) {
    this._monacoEditor?.updateOptions(options);
  }

  createRenderRoot() {
    const root = this.attachShadow({ mode: "open" });

    const st = document.createElement('style')
    st.innerHTML = monacoStyles
    root.append(st)


    //document.querySelectorAll("link[rel='stylesheet']").forEach(
    //  i => {
    //    log(`Cloning Stylesheet: `, i);
    //    root.appendChild(i.cloneNode(true));
    //  });
    //const link = document.createElement('link')
    //link.rel = 'stylesheet'
    // link.href = monacoStyles
    //log(monacoStyles)

    this.style.boxSizing = 'border-box';
    this.style.display = 'flex';
    this.style.alignItems = 'stretch';
    this.style.justifyContent = 'stretch';
    this.style.overflow = 'hidden'
    this.style.position = 'relative'
    this.style.width = '100%'

    const container = document.createElement("div");
    container.id = "container"
    container.style.display = 'flex';
    container.style.overflow ='hidden';
    container.style.flexGrow = '1';
    container.style.position = 'absolute';
    container.style.boxSizing = 'border-box';

    const s = document.createElement('style')
    s.innerHTML = '* {box-sizing: border-box!important;}'
    root.appendChild(s);

    container.style.left = '0'
    container.style.top = '0'
    container.style.right = '0'
    container.style.bottom = '0'

    const slot = document.createElement("slot");
    slot.name = "text"
    slot.style.display = "none"
    slot.addEventListener("slotchange", this._onSlotChange.bind(this));

    root.append(container, slot);
    this.container = container;
    const model = editor.createModel(
      this.value,
      this.language,
      Uri.parse(this.modelUri)
    );

    const opts: editor.IStandaloneEditorConstructionOptions = {
      automaticLayout: true,
      theme: this.theme,
      model
    };

    const possibleOpts: Record<string, string> = {
      automaticLayout: "automatic-layout",
      lineNumbers: "line-numbers",
      roundedSelection: "rounded-selection",
      scrollBeyondLastLine: "scroll-beyond-last-line",
      readOnly: "read-only",
      wordWrap: "word-wrap",
      wordWrapColumn: "word-wrap-column",
      wrappingIndent: "wrapping-indent",
    };

    for (const key in possibleOpts) {
      const value = this.getAttribute(possibleOpts[key]);
      if (value)
        opts[key as keyof typeof opts] = value;
    }

    this._monacoEditor = editor.create(this.container!, opts);
    this.dispatchEvent(
      new CustomEvent("ready", {
        detail: {
          element: this,
          editor: this._monacoEditor,
        },
      })
    );

    return root;
  }
  protected async _onSlotChange(event: Event){
     log('slot change', event.target)
     try {
       const slot = event.target as HTMLSlotElement;
       const nodes = slot.assignedNodes({ flatten: true });
       const value = extractTextFromScripts(nodes);
       log(`slot value changed:`, value);
       this.editor?.setValue(value);
       this._monacoEditor?.layout()
     } catch (e) {
       console.error(`Failed to parse statement`, e);
     }
   }
}
